import { GraphEntityError } from "@clockwise/schema";

type GenericGraphQLObject = { __typename: string };

export type Errorable<T extends GenericGraphQLObject> = T | GraphEntityError;

export function isError<T extends GenericGraphQLObject>(
  value: Errorable<T>,
): value is GraphEntityError {
  return value.__typename === "GraphEntityError";
}

export function isValue<T extends GenericGraphQLObject>(
  value: Errorable<T>,
): value is Exclude<T, GraphEntityError> {
  return !isError(value);
}

function getValueFromUnion<
  T extends Exclude<GenericGraphQLObject, GraphEntityError>,
  K extends T["__typename"]
>(value: T | null, typename: K): Extract<T, { __typename: K }> | null {
  if (!value) {
    return null;
  }

  const isOneTypeOfUnionType = (value: T): value is Extract<T, { __typename: K }> =>
    value.__typename === typename;

  return isOneTypeOfUnionType(value) ? value : null;
}

// Function signature overloading
export function getValue<
  T extends GenericGraphQLObject,
  K extends Exclude<T, GraphEntityError>["__typename"]
>(
  value: Errorable<T> | null | undefined,
  typename: K,
): Extract<Exclude<T, GraphEntityError>, { __typename: K }> | null;
export function getValue<T extends GenericGraphQLObject>(
  value: Errorable<T> | null | undefined,
): Exclude<T, GraphEntityError> | null;

export function getValue(value: any, typename?: any): any {
  if (!value) {
    return null;
  }

  const nonErrorableValue = isValue(value) ? value : null;

  if (!nonErrorableValue) {
    return null;
  }

  return typename ? getValueFromUnion(nonErrorableValue, typename) : nonErrorableValue;
}

export function getError<T extends GenericGraphQLObject>(
  value: Errorable<T> | null | undefined,
): GraphEntityError | null {
  if (!value) {
    return null;
  }

  return isError(value) ? value : null;
}
